import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { useProject } from "@/components/AppShell";
import { Logo } from "@/components/Logo";
import { Printer, ArrowLeft } from "lucide-react";
import { MetricInfo } from "@/components/MetricInfo";

const LEVEL_ORDER = { error: 0, warning: 1, notice: 2 };
const LEVEL_LABEL = { error: "Errors", warning: "Warnings", notice: "Notices" };

export default function AuditReport() {
    const project = useProject();
    const nav = useNavigate();
    const [audit, setAudit] = useState(null);

    useEffect(() => {
        if (!project) return;
        client.get(`/projects/${project.id}/audit/latest`).then((r) => setAudit(r.data));
    }, [project]);

    if (!project || !audit) return <div className="text-muted-foreground font-mono text-sm p-8">loading…</div>;
    const cwv = audit.core_web_vitals;
    const grouped = audit.issues.reduce((acc, i) => { (acc[i.level] ||= []).push(i); return acc; }, {});
    const levels = Object.keys(grouped).sort((a, b) => LEVEL_ORDER[a] - LEVEL_ORDER[b]);

    return (
        <div className="report-view bg-background text-foreground min-h-screen" data-testid="audit-report-view">
            {/* Print-hidden toolbar */}
            <div className="print:hidden sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
                <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button data-testid="report-back-btn" onClick={() => nav("/app/audit")}
                            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2">
                        <ArrowLeft className="w-4 h-4" /> Back to audit
                    </button>
                    <button data-testid="report-print-btn" onClick={() => window.print()}
                            className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2">
                        <Printer className="w-4 h-4" /> Download as PDF
                    </button>
                </div>
            </div>

            {/* Printable report */}
            <main className="report-page max-w-4xl mx-auto px-8 py-12">
                {/* Header */}
                <header className="pb-6 mb-8 border-b border-border flex items-start justify-between">
                    <div>
                        <Logo className="h-16 w-auto mb-4 print:h-12" />
                        <div className="overline">Site Audit Report</div>
                        <h1 className="font-display text-3xl tracking-tight mt-1">{project.name}</h1>
                        <div className="text-sm text-muted-foreground font-mono mt-1">{project.domain}</div>
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                        <div className="uppercase tracking-widest">Generated</div>
                        <div className="font-mono mt-1">{new Date(audit.run_at).toLocaleString()}</div>
                        <div className="font-mono">{audit.pages_crawled.toLocaleString()} pages crawled</div>
                    </div>
                </header>

                {/* Executive summary */}
                <section className="mb-10">
                    <div className="overline mb-3">Executive summary</div>
                    <div className="grid grid-cols-4 gap-4">
                        <div className="border border-border p-5">
                            <div className="text-xs text-muted-foreground flex items-center gap-1">Health Score <span className="print:hidden"><MetricInfo metricKey="health_score" /></span></div>
                            <div className="font-mono text-4xl text-primary mt-1">{audit.health_score}<span className="text-lg text-muted-foreground">/100</span></div>
                        </div>
                        <div className="border border-border p-5">
                            <div className="text-xs text-muted-foreground flex items-center gap-1">Errors <span className="print:hidden"><MetricInfo metricKey="audit_errors" /></span></div>
                            <div className="font-mono text-4xl text-destructive mt-1">{audit.errors}</div>
                        </div>
                        <div className="border border-border p-5">
                            <div className="text-xs text-muted-foreground flex items-center gap-1">Warnings <span className="print:hidden"><MetricInfo metricKey="audit_warnings" /></span></div>
                            <div className="font-mono text-4xl text-warning mt-1">{audit.warnings}</div>
                        </div>
                        <div className="border border-border p-5">
                            <div className="text-xs text-muted-foreground flex items-center gap-1">Notices <span className="print:hidden"><MetricInfo metricKey="audit_notices" /></span></div>
                            <div className="font-mono text-4xl text-secondary mt-1">{audit.notices}</div>
                        </div>
                    </div>
                </section>

                {/* Core Web Vitals */}
                <section className="mb-10 break-inside-avoid">
                    <div className="overline mb-3">Core Web Vitals</div>
                    <table className="w-full border border-border text-sm">
                        <thead className="bg-card/40 text-xs text-muted-foreground">
                            <tr>
                                <th className="text-left p-3">Metric</th>
                                <th className="text-right p-3">Value</th>
                                <th className="text-right p-3">Target</th>
                                <th className="text-right p-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                ["Largest Contentful Paint (LCP)", `${cwv.lcp}s`, "≤ 2.5s", cwv.lcp <= 2.5, "lcp"],
                                ["Cumulative Layout Shift (CLS)", cwv.cls, "≤ 0.1", cwv.cls <= 0.1, "cls"],
                                ["Interaction to Next Paint (INP)", `${cwv.inp}ms`, "≤ 200ms", cwv.inp <= 200, "inp"],
                                ["Mobile PageSpeed", `${cwv.mobile_score}/100`, "≥ 90", cwv.mobile_score >= 90, "mobile_pagespeed"],
                                ["Desktop PageSpeed", `${cwv.desktop_score}/100`, "≥ 90", cwv.desktop_score >= 90, "desktop_pagespeed"],
                            ].map(([label, val, tgt, ok, mk]) => (
                                <tr key={label} className="border-t border-border">
                                    <td className="p-3 flex items-center gap-1">{label} <span className="print:hidden"><MetricInfo metricKey={mk} /></span></td>
                                    <td className="p-3 text-right font-mono">{val}</td>
                                    <td className="p-3 text-right text-xs text-muted-foreground font-mono">{tgt}</td>
                                    <td className={`p-3 text-right text-xs font-mono ${ok ? "text-success" : "text-warning"}`}>
                                        {ok ? "PASS" : "NEEDS WORK"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>

                {/* Issues */}
                <section className="mb-10">
                    <div className="overline mb-3">Findings by severity</div>
                    {levels.map((lvl) => (
                        <div key={lvl} className="mb-6 break-inside-avoid">
                            <div className="text-sm font-medium mb-2 flex items-center gap-2">
                                <span className={`w-2 h-2 ${lvl === "error" ? "bg-destructive" : lvl === "warning" ? "bg-warning" : "bg-secondary"}`} />
                                {LEVEL_LABEL[lvl]} <span className="text-muted-foreground font-mono text-xs">({grouped[lvl].length})</span>
                            </div>
                            <table className="w-full border border-border text-sm">
                                <thead className="bg-card/40 text-xs text-muted-foreground">
                                    <tr>
                                        <th className="text-left p-3">Issue</th>
                                        <th className="text-left p-3">Recommendation</th>
                                        <th className="text-right p-3">Count</th>
                                        <th className="text-right p-3">Severity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {grouped[lvl].map((iss) => (
                                        <tr key={iss.id} className="border-t border-border align-top">
                                            <td className="p-3 font-medium">{iss.title}</td>
                                            <td className="p-3 text-muted-foreground">{iss.fix}</td>
                                            <td className="p-3 text-right font-mono">{iss.count}</td>
                                            <td className="p-3 text-right text-xs uppercase tracking-wider text-muted-foreground">{iss.severity}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </section>

                {/* Footer */}
                <footer className="mt-12 pt-6 border-t border-border text-xs text-muted-foreground flex justify-between items-end">
                    <div>
                        <div className="font-medium text-foreground">Phase 2 Labs Marketing</div>
                        <div>1-855-2PHASE2 · contact@phase2labs.net · phase2labs.net</div>
                    </div>
                    <div className="text-right font-mono">
                        <div>InPhase · Site Audit</div>
                        <div>{new Date().toISOString().slice(0, 10)}</div>
                    </div>
                </footer>
            </main>

            {/* Print CSS */}
            <style>{`
                @media print {
                    @page { size: A4; margin: 12mm; }
                    body, html, .report-view { background: white !important; color: #0a0a0a !important; }
                    .report-view * { color: inherit; }
                    .overline { color: #6b7280 !important; }
                    .text-muted-foreground { color: #6b7280 !important; }
                    .text-primary { color: #0369a1 !important; }
                    .text-destructive { color: #b91c1c !important; }
                    .text-warning { color: #b45309 !important; }
                    .text-secondary { color: #0891b2 !important; }
                    .text-success { color: #047857 !important; }
                    .border-border { border-color: #d1d5db !important; }
                    .bg-primary { background: #0369a1 !important; }
                    .bg-destructive { background: #b91c1c !important; }
                    .bg-warning { background: #b45309 !important; }
                    .bg-secondary { background: #0891b2 !important; }
                    .bg-card\\/40 { background: #f9fafb !important; }
                    section, table, tr { page-break-inside: avoid; }
                    .print\\:hidden { display: none !important; }
                    .print\\:h-12 { height: 3rem !important; }
                }
            `}</style>
        </div>
    );
}
